import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import * as moment from 'moment';
import { Filter } from 'src/app/core/services/base/database.service';
import { Column, TableSettings } from '../../table.component';

export interface AdvancedFilterRow {
  field: string;
  operator: string;
  value: string;
}

@Component({
  selector: 'app-advanced-filters',
  templateUrl: './advanced-filters.component.html',
  styleUrls: ['./advanced-filters.component.scss'],
})
export class AdvancedFiltersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() settings!: TableSettings;
  @Input() activeFilters: Filter[] = [];
  @Output() filtersChange = new EventEmitter<Filter[]>();

  rows: AdvancedFilterRow[] = [];

  dateOperator: 'gt' | 'lt' | 'bt' = 'bt';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  enableDateFilter = false;

  availableColumns: Column[] = [];

  // All text operators use ilike-based matching → always case-insensitive
  textOperators = [
    { value: 'contains', label: 'Contains' },
    { value: 'ilike', label: 'Is' },
    { value: 'startswith', label: 'Starts with' },
    { value: 'endswith', label: 'Ends with' },
    { value: 'empty', label: 'Is empty' },
  ];

  dateOperators = [
    { value: 'gt', label: 'After' },
    { value: 'lt', label: 'Before' },
    { value: 'bt', label: 'Between' },
  ];

  ngOnInit(): void {
    this._loadColumns();
    this._restoreFromActiveFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings']) {
      this._loadColumns();
    }
    if (changes['activeFilters']) {
      this._restoreFromActiveFilters();
    }
  }

  ngOnDestroy(): void {}

  private _loadColumns(): void {
    if (!this.settings) return;
    // Exclude JSON columns (handled by per-column filters) and non-filterable types
    this.availableColumns = this.settings.columns.filter(
      (c) =>
        c.type !== 'accordeon' &&
        c.type !== 'component' &&
        c.type !== 'json' &&
        c.id !== 'acc'
    );
  }

  private _restoreFromActiveFilters(): void {
    if (!this.activeFilters?.length) return;

    const dateFilters = this.activeFilters.filter(
      (f) => f.column === 'created_at' && (f.operator === 'gte' || f.operator === 'lte')
    );
    const otherFilters = this.activeFilters.filter(
      (f) => f.column !== 'created_at'
    );

    if (dateFilters.length) {
      this.enableDateFilter = true;
      const from = dateFilters.find((f) => f.operator === 'gte');
      const to = dateFilters.find((f) => f.operator === 'lte');
      this.dateFrom = from ? new Date(from.value as string) : null;
      this.dateTo = to ? new Date(to.value as string) : null;
      this.dateOperator = from && to ? 'bt' : from ? 'gt' : 'lt';
    }

    if (otherFilters.length) {
      this.rows = otherFilters.map((f) => ({
        field: f.column,
        operator: f.operator as string,
        value: f.value as string,
      }));
    }
  }

  addRow(): void {
    const firstCol = this.availableColumns[0];
    this.rows = [
      ...this.rows,
      { field: firstCol?.id ?? '', operator: 'contains', value: '' },
    ];
  }

  removeRow(index: number): void {
    this.rows = this.rows.filter((_, i) => i !== index);
  }

  trackByIndex(index: number): number {
    return index;
  }

  isEmptyOperator(operator: string): boolean {
    return operator === 'empty';
  }

  apply(): void {
    const filters: Filter[] = [];

    for (const row of this.rows) {
      if (!row.field) continue;
      if (!this.isEmptyOperator(row.operator) && !row.value?.trim()) continue;

      filters.push({
        column: row.field,
        operator: row.operator as any,
        value: this.isEmptyOperator(row.operator) ? null : row.value,
        visible: true,
      });
    }

    if (this.enableDateFilter) {
      if ((this.dateOperator === 'gt' || this.dateOperator === 'bt') && this.dateFrom) {
        filters.push({
          column: 'created_at',
          operator: 'gte',
          value: this.dateFrom.toISOString(),
          visible: true,
        });
      }
      if ((this.dateOperator === 'lt' || this.dateOperator === 'bt') && this.dateTo) {
        filters.push({
          column: 'created_at',
          operator: 'lte',
          value: this.dateTo.toISOString(),
          visible: true,
        });
      }
    }

    this.filtersChange.emit(filters);
  }

  clear(): void {
    this.rows = [];
    this.enableDateFilter = false;
    this.dateFrom = null;
    this.dateTo = null;
    this.dateOperator = 'bt';
    this.filtersChange.emit([]);
  }

  getActiveCount(): number {
    return this.activeFilters?.length ?? 0;
  }

  formatDate(d: Date | null): string {
    if (!d) return '';
    return moment(d).format('DD/MM/YYYY');
  }
}
